import { Component, OnInit, OnChanges, ViewChild, ElementRef, Input, ViewEncapsulation } from '@angular/core';
import * as d3 from 'd3';

@Component({
    selector: 'app-barchart',
    templateUrl: './bar-chart.component.html',
    styleUrls: ['./bar-chart.component.css'],
    encapsulation: ViewEncapsulation.None
})
export class BarChartComponent implements OnInit, OnChanges {
  @ViewChild('chart', { static: true }) private chartContainer: ElementRef;
  @Input() private data: Array<any>;
  @Input() private configuration: { xMap: string; yMap: string };
  private margin: any = { top: 20, bottom: 60, left: 20, right: 20 };
  private chart: any;
  private width: number;
  private height: number;
  private xScale: any;
  private yScale: any;
  private colors: any;
  private xAxis: any;
  private yAxis: any;

  constructor() {}

  ngOnInit() {
    this.createChart();
    if (this.data) {
      this.updateChart();
    }
  }

  ngOnChanges() {
    if (this.chart) {
      this.updateChart();
    }
  }

  createChart() {
    const element = this.chartContainer.nativeElement;
    this.width = element.offsetWidth - this.margin.left - this.margin.right;
    this.height = element.offsetHeight - this.margin.top - this.margin.bottom;
    const svg = d3
      .select(element)
      .append('svg')
      .attr('width', element.offsetWidth)
      .attr('height', element.offsetHeight);

    // chart plot area
    this.chart = svg
      .append('g')
      .attr('class', 'bars')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    // define X & Y domains
    const xDomain = this.data.map(d => d[this.configuration.xMap]);
    const yDomain = [0, d3.max(this.data, d => d[this.configuration.yMap])];

    // create scales
    this.xScale = d3
      .scaleBand()
      .range([0, this.width])
      .padding(1)
      .round(true)
      .domain(xDomain);
    this.yScale = d3
      .scaleLinear()
      .domain(yDomain)
      .range([this.height, 0]);

    // bar colors
    this.colors = ['green', 'blue', 'red', 'yellow'];

    // x & y axis
    this.xAxis = svg
      .append('g')
      .attr('class', 'axis axis-x')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top + this.height})`)
      .call(d3.axisBottom(this.xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-40)');
    this.yAxis = svg
      .append('g')
      .attr('class', 'axis axis-y')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`)
      .call(d3.axisLeft(this.yScale));
  }

  updateChart() {
    // update scales & axis
    this.xScale.domain(this.data.map(d => d[this.configuration.xMap]));
    this.yScale.domain([0, d3.max(this.data, d => d[this.configuration.yMap])]);
    // this.colors.domain([0, this.data.length]);
    this.xAxis.transition().call(d3.axisBottom(this.xScale));
    this.yAxis.transition().call(d3.axisLeft(this.yScale));

    const update = this.chart.selectAll('.bar').data(this.data);

    // remove exiting bars
    update.exit().remove();

    // update existing bars
    this.chart
      .selectAll('.bar')
      .transition()
      .attr('x', d => this.xScale(d[this.configuration.xMap]))
      .attr('y', d => this.yScale(d[this.configuration.yMap]))
      .attr('width', () => 25)
      .attr('height', d => this.height - this.yScale(d[this.configuration.yMap]))
      .style('fill', (_d, i) => this.colors[i]);

    // add new bars
    update
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => this.xScale(d[this.configuration.xMap]))
      .attr('y', d => this.yScale(d[this.configuration.yMap]))
      .attr('width', 30)
      .style('fill', (_d, i) => this.colors[i])
      .transition()
      .delay((_d, i) => i * 10)
      .attr('height', d => this.height - this.yScale(d[this.configuration.yMap]));
  }
}
